import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Portal } from 'react-portal'

import { HighlightedText } from '@sketch/components'

import { getCommandBarRenderState, getVisibleCommandBarItems } from '../utils'
import { CommandBarItem } from '../types'

import * as S from './CommandBar.styles'

import { ReactComponent as SearchIcon } from '@sketch/icons/img-magnifying-glass-17'

const stopNextEnterKeyUp = () => {
  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
  }

  window.addEventListener('keyup', handleKeyUp, {
    capture: true,
    once: true,
  })
}

interface CommandBarProps {
  isOpen: boolean
  items: CommandBarItem[]
  loading: boolean
  error?: Error
  query: string
  onQueryChange: (query: string) => void
  onClose: () => void
  onRunItem: (item: CommandBarItem, rank: number) => void
}

export const CommandBar = ({
  isOpen,
  items,
  loading,
  error,
  query,
  onQueryChange,
  onClose,
  onRunItem,
}: CommandBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const resultItemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const activeItemIdRef = useRef<string | null>(null)
  const hasMouseMovedOverResultsRef = useRef(false)
  const previousQueryRef = useRef(query)
  const [activeIndex, setActiveIndex] = useState(0)

  const visibleItems = useMemo(
    () => getVisibleCommandBarItems(items, query),
    [items, query]
  )

  const { renderedItemIds, renderedItems, sections } = useMemo(() => {
    return getCommandBarRenderState(visibleItems, query)
  }, [query, visibleItems])

  // Opening should focus the search field without letting a stationary cursor
  // over the results immediately steal the active keyboard selection.
  useEffect(() => {
    if (isOpen) {
      hasMouseMovedOverResultsRef.current = false
      window.setTimeout(() => inputRef.current?.focus(), 0)
    } else {
      activeItemIdRef.current = null
    }
  }, [isOpen])

  // Preserve keyboard selection across async result updates, but reset to the
  // first result when the user intentionally changes the query.
  useEffect(() => {
    if (query === '' && activeItemIdRef.current === null) {
      setActiveIndex(0)
    } else {
      setActiveIndex(currentIndex => {
        if (previousQueryRef.current !== query) {
          previousQueryRef.current = query
          activeItemIdRef.current = renderedItems[0]?.id || null
          return 0
        }

        const activeItemId = activeItemIdRef.current

        if (!activeItemId) {
          return 0
        }

        const nextIndex = renderedItems.findIndex(
          item => item.id === activeItemId
        )

        if (nextIndex !== -1) {
          return nextIndex
        }

        const clampedIndex = Math.min(
          currentIndex,
          Math.max(renderedItems.length - 1, 0)
        )
        activeItemIdRef.current = renderedItems[clampedIndex]?.id || null
        return clampedIndex
      })
    }
  }, [query, renderedItemIds, renderedItems])

  // Keyboard navigation should keep the active row visible as the user moves
  // through a scrollable result list.
  useEffect(() => {
    const activeItem = resultItemRefs.current[activeIndex]

    if (!activeItem) {
      return
    }

    activeItem.scrollIntoView({
      block: 'nearest',
    })
  }, [activeIndex])

  if (!isOpen) {
    return null
  }

  const runActiveItem = () => {
    const item = renderedItems[activeIndex]

    if (!item || item.disabledReason) {
      return
    }

    onRunItem(item, activeIndex)
  }

  const updateActiveIndex = (getNextIndex: (index: number) => number) => {
    setActiveIndex(index => {
      const nextIndex = getNextIndex(index)
      activeItemIdRef.current = renderedItems[nextIndex]?.id || null
      return nextIndex
    })
  }

  const handleResultItemMouseEnter = (index: number) => {
    if (!hasMouseMovedOverResultsRef.current) {
      return
    }

    updateActiveIndex(() => index)
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (renderedItems.length === 0) {
        return
      }
      updateActiveIndex(index => Math.min(index + 1, renderedItems.length - 1))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (renderedItems.length === 0) {
        return
      }
      updateActiveIndex(index => Math.max(index - 1, 0))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      stopNextEnterKeyUp()
      runActiveItem()
    }
  }

  let itemIndex = -1

  return (
    <Portal>
      <S.Overlay onMouseDown={onClose}>
        <S.Dialog
          role="dialog"
          aria-modal="true"
          aria-label="Command bar"
          onMouseDown={event => event.stopPropagation()}
        >
          <S.SearchRow>
            <S.SearchIconWrapper>
              <SearchIcon />
            </S.SearchIconWrapper>
            <S.Input
              ref={inputRef}
              aria-label="Search commands"
              placeholder="Search commands, documents, projects"
              value={query}
              onChange={event => onQueryChange(event.target.value)}
              onKeyDown={handleInputKeyDown}
            />
          </S.SearchRow>

          <S.Results
            onMouseMove={() => {
              hasMouseMovedOverResultsRef.current = true
            }}
          >
            {sections.map(([section, sectionItems]) => (
              <S.Section key={section}>
                <S.SectionTitle>{section}</S.SectionTitle>
                {sectionItems.map(item => {
                  itemIndex += 1
                  const currentIndex = itemIndex
                  const Icon = item.icon
                  const iconNode = item.iconNode

                  return (
                    <S.ResultItem
                      key={item.id}
                      ref={element => {
                        resultItemRefs.current[currentIndex] = element
                      }}
                      $active={currentIndex === activeIndex}
                      disabled={Boolean(item.disabledReason)}
                      onMouseEnter={() =>
                        handleResultItemMouseEnter(currentIndex)
                      }
                      onClick={() => onRunItem(item, currentIndex)}
                    >
                      <S.ResultIcon>
                        {iconNode || (Icon && <Icon />)}
                      </S.ResultIcon>
                      <S.ResultText>
                        <S.ResultTitle>
                          <HighlightedText search={query}>
                            {item.title}
                          </HighlightedText>
                        </S.ResultTitle>
                        {item.subtitle && (
                          <S.ResultSubtitle>
                            <HighlightedText search={query}>
                              {item.subtitle}
                            </HighlightedText>
                          </S.ResultSubtitle>
                        )}
                        {item.disabledReason && (
                          <S.ResultSubtitle>
                            {item.disabledReason}
                          </S.ResultSubtitle>
                        )}
                      </S.ResultText>
                      {item.shortcut && (
                        <S.Shortcut>{item.shortcut}</S.Shortcut>
                      )}
                    </S.ResultItem>
                  )
                })}
              </S.Section>
            ))}

            {loading && <S.StateMessage>Searching...</S.StateMessage>}
            {error && (
              <S.StateMessage>
                Some remote results could not be loaded.
              </S.StateMessage>
            )}
            {!loading && visibleItems.length === 0 && (
              <S.StateMessage>No results</S.StateMessage>
            )}
          </S.Results>
        </S.Dialog>
      </S.Overlay>
    </Portal>
  )
}
